import { CSSProperties, HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations/index.ts';

const MOBILE_COLUMN_WIDTH = 140;
const MOBILE_COLUMN_WIDTH_CSS = `${MOBILE_COLUMN_WIDTH}px`;
const SCROLL_EDGE_TOLERANCE = 1;

const tableCss = css({
	tableLayout: 'fixed',
	width: '100%',
	padding: 0,
	border: 0,
	borderCollapse: 'collapse',
	borderSpacing: 0,
	display: 'table',
	[media.below.sm]: {
		minWidth: '100%',
		width: 'max-content',
	},
});

const tableContainerCss = css({
	width: '100%',
});

const scrollContainerCss = css({
	width: '100%',
	[media.below.sm]: {
		overflowX: 'auto',
		overflowY: 'hidden',
		overscrollBehaviorInline: 'contain',
		scrollBehavior: 'smooth',
		scrollbarWidth: 'none',
	},
});

const controlsWrapperCss = css({
	display: 'none',
	[media.below.sm]: {
		display: 'flex',
		gap: token('space.100'),
		justifyContent: 'flex-end',
		marginBlockStart: token('space.150'),
	},
});

const tableControlButtonCss = css({
	alignItems: 'center',
	backgroundColor: wacToken('color.neutral.light0'),
	border: `1px solid ${wacToken('color.primary.black')}`,
	borderRadius: '50%',
	color: wacToken('color.primary.black'),
	cursor: 'pointer',
	display: 'inline-flex',
	height: '24px',
	justifyContent: 'center',
	padding: 0,
	width: '24px',
	'&:hover:not(:disabled)': {
		backgroundColor: wacToken('color.neutral.light100'),
	},
	'&:active:not(:disabled)': {
		transform: 'scale(0.97)',
	},
	'&:disabled': {
		borderColor: wacToken('color.neutral.light400'),
		color: wacToken('color.neutral.light400'),
		cursor: 'default',
	},
	'&:focus-visible:not(:disabled)': {
		outline: `2px solid ${token('color.border.focused')}`,
		outlineOffset: '2px',
	},
});

const getColumnCount = (table: HTMLTableElement) => {
	const rows = Array.from(table.querySelectorAll('tr'));
	const getTableCells = (row: HTMLTableRowElement) =>
		Array.from(row.children).filter((child) => child.matches('th, td'));
	const rowWithColumns = rows.find((row) => getTableCells(row).length > 1);

	if (!rowWithColumns) {
		return 0;
	}

	return getTableCells(rowWithColumns).length;
};

const getLogicalMaxScrollLeft = (dataColumnCount: number) =>
	Math.max(dataColumnCount - 1, 0) * MOBILE_COLUMN_WIDTH;

const getScrollState = (scrollContainer: HTMLDivElement, dataColumnCount: number) => {
	const measuredMaxScrollLeft =
		scrollContainer.scrollWidth === 0 && scrollContainer.clientWidth === 0
			? getLogicalMaxScrollLeft(dataColumnCount)
			: Math.max(scrollContainer.scrollWidth - scrollContainer.clientWidth, 0);
	const scrollLeft = Math.min(Math.max(scrollContainer.scrollLeft, 0), measuredMaxScrollLeft);

	return {
		scrollLeft,
		maxScrollLeft: measuredMaxScrollLeft,
	};
};

const getNextColumnScrollLeft = (scrollLeft: number, maxScrollLeft: number) =>
	Math.min(
		(Math.floor((scrollLeft + SCROLL_EDGE_TOLERANCE) / MOBILE_COLUMN_WIDTH) + 1) *
			MOBILE_COLUMN_WIDTH,
		maxScrollLeft,
	);

const getPreviousColumnScrollLeft = (scrollLeft: number) =>
	Math.max(
		Math.ceil((scrollLeft - SCROLL_EDGE_TOLERANCE) / MOBILE_COLUMN_WIDTH) * MOBILE_COLUMN_WIDTH -
			MOBILE_COLUMN_WIDTH,
		0,
	);

type GenericTableStyle = CSSProperties & {
	'--generic-table-mobile-column-width': string;
	'--generic-table-visible-width': string;
};

interface TableScrollState {
	scrollLeft: number;
	maxScrollLeft: number;
}

interface GenericTableControlsProps {
	isPreviousDisabled: boolean;
	isNextDisabled: boolean;
	onPrevious: () => void;
	onNext: () => void;
}

const GenericTableControls = ({
	isPreviousDisabled,
	isNextDisabled,
	onPrevious,
	onNext,
}: GenericTableControlsProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireTableControlClicked = useCallback(
		(direction: 'previous' | 'next') => {
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'tableControlButton',
				eventType: 'ui',
				eventComponent: 'button',
				source: 'generic-table',
				attributes: {
					direction,
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const handlePrevious = useCallback(() => {
		fireTableControlClicked('previous');
		onPrevious();
	}, [fireTableControlClicked, onPrevious]);

	const handleNext = useCallback(() => {
		fireTableControlClicked('next');
		onNext();
	}, [fireTableControlClicked, onNext]);

	return (
		<div
			aria-label={intl.formatMessage({
				id: 'generic-table.navigation',
				defaultMessage: 'Table navigation',
			})}
			css={controlsWrapperCss}
			data-testid="generic-table-controls"
			role="group"
		>
			<button
				aria-label={intl.formatMessage({
					id: 'generic-table.previous-column',
					defaultMessage: 'Previous column',
				})}
				css={tableControlButtonCss}
				disabled={isPreviousDisabled}
				onClick={handlePrevious}
				type="button"
			>
				<ChevronLeftIcon label="" spacing="none" />
			</button>
			<button
				aria-label={intl.formatMessage({
					id: 'generic-table.next-column',
					defaultMessage: 'Next column',
				})}
				css={tableControlButtonCss}
				disabled={isNextDisabled}
				onClick={handleNext}
				type="button"
			>
				<ChevronRightIcon label="" spacing="none" />
			</button>
		</div>
	);
};

export const GenericTable = ({ children, style, ...props }: HTMLAttributes<HTMLTableElement>) => {
	const tableRef = useRef<HTMLTableElement>(null);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const [scrollState, setScrollState] = useState<TableScrollState>({
		scrollLeft: 0,
		maxScrollLeft: 0,
	});
	const [dataColumnCount, setDataColumnCount] = useState(0);
	const [visibleWidth, setVisibleWidth] = useState('100%');

	const syncScrollState = useCallback(
		(nextDataColumnCount = dataColumnCount) => {
			const scrollContainer = scrollContainerRef.current;
			if (!scrollContainer) {
				return;
			}

			setScrollState(getScrollState(scrollContainer, nextDataColumnCount));
			setVisibleWidth(
				scrollContainer.clientWidth > 0 ? `${scrollContainer.clientWidth}px` : '100%',
			);
		},
		[dataColumnCount],
	);

	useEffect(() => {
		const table = tableRef.current;
		if (!table) {
			return;
		}

		const columnCount = getColumnCount(table);
		const nextDataColumnCount = Math.max(columnCount - 1, 0);

		setDataColumnCount(nextDataColumnCount);
		syncScrollState(nextDataColumnCount);
	}, [children, syncScrollState]);

	useEffect(() => {
		const scrollContainer = scrollContainerRef.current;
		if (!scrollContainer) {
			return;
		}

		syncScrollState();

		const handleResize = () => {
			syncScrollState();
		};

		window.addEventListener('resize', handleResize);

		if (typeof ResizeObserver === 'undefined') {
			return () => {
				window.removeEventListener('resize', handleResize);
			};
		}

		const resizeObserver = new ResizeObserver(handleResize);
		resizeObserver.observe(scrollContainer);

		if (tableRef.current) {
			resizeObserver.observe(tableRef.current);
		}

		return () => {
			window.removeEventListener('resize', handleResize);
			resizeObserver.disconnect();
		};
	}, [syncScrollState]);

	const scrollToPosition = useCallback(
		(nextScrollLeft: number) => {
			const scrollContainer = scrollContainerRef.current;
			if (!scrollContainer) {
				return;
			}

			const { maxScrollLeft } = getScrollState(scrollContainer, dataColumnCount);
			const scrollLeft = Math.min(Math.max(nextScrollLeft, 0), maxScrollLeft);
			scrollContainer.scrollTo({
				top: 0,
				left: scrollLeft,
				behavior: 'smooth',
			});
		},
		[dataColumnCount],
	);

	const handlePrevious = useCallback(() => {
		scrollToPosition(getPreviousColumnScrollLeft(scrollState.scrollLeft));
	}, [scrollState.scrollLeft, scrollToPosition]);

	const handleNext = useCallback(() => {
		scrollToPosition(getNextColumnScrollLeft(scrollState.scrollLeft, scrollState.maxScrollLeft));
	}, [scrollState.maxScrollLeft, scrollState.scrollLeft, scrollToPosition]);

	const handleScroll = useCallback(() => {
		syncScrollState();
	}, [syncScrollState]);

	const isPreviousDisabled = scrollState.scrollLeft <= SCROLL_EDGE_TOLERANCE;
	const isNextDisabled =
		scrollState.maxScrollLeft <= SCROLL_EDGE_TOLERANCE ||
		scrollState.scrollLeft >= scrollState.maxScrollLeft - SCROLL_EDGE_TOLERANCE;
	const shouldShowControls = dataColumnCount > 1 && (!isPreviousDisabled || !isNextDisabled);

	return (
		<div css={tableContainerCss}>
			<div
				css={scrollContainerCss}
				data-testid="generic-table-scroll-container"
				onScroll={handleScroll}
				ref={scrollContainerRef}
			>
				<table
					{...props}
					css={tableCss}
					ref={tableRef}
					style={
						{
							'--generic-table-mobile-column-width': MOBILE_COLUMN_WIDTH_CSS,
							'--generic-table-visible-width': visibleWidth,
							...style,
						} as GenericTableStyle
					}
				>
					{children}
				</table>
			</div>
			{shouldShowControls && (
				<GenericTableControls
					isPreviousDisabled={isPreviousDisabled}
					isNextDisabled={isNextDisabled}
					onPrevious={handlePrevious}
					onNext={handleNext}
				/>
			)}
		</div>
	);
};
